import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../config/api';
import { Task } from '../../types/task';
import { FiPlus, FiRefreshCw } from 'react-icons/fi';

const PRIORITY_BADGE: Record<string, string> = {
  urgent: 'badge-error',
  high:   'badge-warning',
  medium: 'badge-info',
  low:    'badge-ghost',
};

const STATUS_BADGE: Record<string, string> = {
  open:        'badge-info',
  in_progress: 'badge-primary',
  completed:   'badge-success',
  cancelled:   'badge-ghost',
};

const formatDate = (d?: string) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const isOverdue = (t: Task) =>
  !!t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed';

const TasksSidebarPanel: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'active' | 'all'>('active');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = filter === 'active' ? '?status=open,in_progress' : '';
      const res = await apiCall(`${ENDPOINTS.TASKS.LIST}${params}`, { method: 'GET' }, token);
      if (res.ok) {
        const data = await res.json();
        setTasks(data.results || data);
      }
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener('tasks:refresh', handler);
    return () => window.removeEventListener('tasks:refresh', handler);
  }, [load]);

  const activeTasks = tasks.filter(t => t.status === 'open' || t.status === 'in_progress');
  const overdueTasks = tasks.filter(isOverdue);

  return (
    <div className="flex flex-col h-full">
      {/* Filter + actions */}
      <div className="px-3 py-2 border-b border-base-300 flex-shrink-0 space-y-2">
        <div className="flex items-center gap-2">
          <div className="join flex-1">
            <button
              className={`btn btn-xs join-item flex-1 ${filter === 'active' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilter('active')}
            >
              Active
            </button>
            <button
              className={`btn btn-xs join-item flex-1 ${filter === 'all' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
          </div>
          <button className="btn btn-xs btn-ghost" onClick={load} title="Refresh">
            <FiRefreshCw className={`text-xs ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            className="btn btn-xs btn-primary"
            onClick={() => navigate('/tasks/new')}
            title="New task"
          >
            <FiPlus />
          </button>
        </div>

        {/* Summary badges */}
        <div className="flex gap-2 flex-wrap">
          <span className="badge badge-sm badge-info gap-1">
            {activeTasks.length} active
          </span>
          {overdueTasks.length > 0 && (
            <span className="badge badge-sm badge-error gap-1">
              {overdueTasks.length} overdue
            </span>
          )}
        </div>
      </div>

      {/* Task list */}
      <div className="flex-1 overflow-y-auto divide-y divide-base-300">
        {loading && (
          <div className="flex justify-center py-8">
            <span className="loading loading-spinner loading-md" />
          </div>
        )}

        {!loading && tasks.length === 0 && (
          <div className="text-center text-base-content/40 py-10 px-4">
            <p className="text-sm">No tasks found</p>
            <button
              className="btn btn-primary btn-sm mt-3"
              onClick={() => navigate('/tasks/new')}
            >
              <FiPlus className="mr-1" /> Create Task
            </button>
          </div>
        )}

        {!loading && tasks.map(task => (
          <div
            key={task.id}
            className="px-3 py-2.5 hover:bg-base-200 cursor-pointer transition-colors"
            onClick={() => navigate(`/tasks?id=${task.id}`)}
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className={`text-sm font-medium leading-tight flex-1 ${task.status === 'completed' ? 'line-through opacity-50' : ''}`}>
                {task.title}
              </p>
              <span className={`badge badge-xs flex-shrink-0 ${PRIORITY_BADGE[task.priority] ?? 'badge-ghost'}`}>
                {task.priority}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className={`badge badge-xs ${STATUS_BADGE[task.status] ?? 'badge-ghost'}`}>
                {task.status.replace('_', ' ')}
              </span>
              {task.patient_name && (
                <span className="text-xs text-base-content/60 truncate max-w-[120px]">
                  {task.patient_name}
                </span>
              )}
              {task.due_date && (
                <span className={`text-xs ml-auto flex-shrink-0 ${isOverdue(task) ? 'text-error font-semibold' : 'text-base-content/50'}`}>
                  {isOverdue(task) ? '⚠ ' : ''}{formatDate(task.due_date)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer link */}
      {tasks.length > 0 && (
        <div className="border-t border-base-300 px-3 py-2 flex-shrink-0">
          <button
            className="btn btn-ghost btn-xs w-full"
            onClick={() => navigate('/tasks')}
          >
            View all tasks →
          </button>
        </div>
      )}
    </div>
  );
};

export default TasksSidebarPanel;
